import { MultiStepFormState } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form-item';
import { SubmitHandlerWithDirtyFields } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form.provider';
import { getDeleteTariffDefaultValues } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/tariff/delete-tariff.form-default-values';
import { DeleteTariffModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/tariff/delete-tariff.model';
import { useState } from 'react';
import { SubmitErrorHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Logger } from '@app/core/logger/logger';
import { useCpmsBoDeleteTariffMutation } from '@service/cpms-bo-tariff.service';

import {
  DeleteTariffContextProvider,
  useTariffContext,
} from '../tariff.context';
import { TariffDeleteFormController } from './tariff-delete-form/tariff-delete-form.controller';

interface TariffDeleteControllerProps {
  onCancel: () => void;
  onSuccess: () => void;
}

export const TariffDeleteController = ({
  onCancel,
  onSuccess,
}: TariffDeleteControllerProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const [deleteTariffMutation] = useCpmsBoDeleteTariffMutation();
  const tariff = useTariffContext();
  const [multiStepFormState, setMultiStepFormState] = useState(
    MultiStepFormState.Default,
  );

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handleError: SubmitErrorHandler<Pick<DeleteTariffModel, 'id'>> = (
    errors,
  ) => {
    Logger.error(errors);
    toast.error(t('$.tariff-delete.error', { error: t('global.unknown') }));
  };

  const handleSubmit: SubmitHandlerWithDirtyFields<
    Pick<DeleteTariffModel, 'id'>
  > = (fieldValues) => {
    setMultiStepFormState(MultiStepFormState.Loading);
    deleteTariffMutation({
      id: fieldValues.id,
    })
      .unwrap()
      .then(() => {
        toast.success(t('$.tariff-delete.success'));
        onSuccess();
      })
      .catch(({ data: { message: errorMessage } }) => {
        toast.error(t('$.tariff-delete.error', { error: errorMessage }));
      })
      .finally(() => setMultiStepFormState(MultiStepFormState.Default));
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  const data = getDeleteTariffDefaultValues(tariff.id);

  return (
    <DeleteTariffContextProvider data={data}>
      <TariffDeleteFormController
        onError={handleError}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        state={multiStepFormState}
      />
    </DeleteTariffContextProvider>
  );
};
